import { FileUploadInput } from '@shared/types/project.type';
import { getClientCredentials } from '../../../../../app/shared/utils/cookie.utils';

/** Allowed extensions for audio deepfake upload (per API doc) */
const AUDIO_DEEPFAKE_EXTENSIONS = ['.wav', '.mp3', '.m4a', '.flac', '.ogg', '.webm'];

/** API requires this value for POST /upload-audio-deepfake */
export const AUDIO_DEEPFAKE_UPLOAD_TYPE = 'AUDIO_DEEPFAKE' as const;

export interface UploadAudioDeepfakeInput {
  file: File;
  projectId: string;
  userId: string;
  uploadType?: typeof AUDIO_DEEPFAKE_UPLOAD_TYPE;
  title?: string;
  domain?: string;
}

export const uploadAudioDeepfake = (
  input: UploadAudioDeepfakeInput,
  onProgress: (progress: number, status: string) => void
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const ext = input.file.name.toLowerCase().slice(input.file.name.lastIndexOf('.'));
    if (!AUDIO_DEEPFAKE_EXTENSIONS.includes(ext)) {
      reject(new Error(`Invalid file format. Allowed: ${AUDIO_DEEPFAKE_EXTENSIONS.join(', ')}`));
      return;
    }

    const formData = new FormData();
    formData.append('file', input.file);
    formData.append('projectId', input.projectId);
    formData.append('userId', input.userId);
    formData.append('uploadType', input.uploadType ?? AUDIO_DEEPFAKE_UPLOAD_TYPE);
    if (input.title) formData.append('title', input.title);
    if (input.domain) formData.append('domain', input.domain);

    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, '') || '';
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/upload-audio-deepfake`);

    const token = localStorage.getItem('accessToken');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    const environment = (localStorage.getItem('SELECTED_ENV') || 'DEV') as 'DEV' | 'PROD';
    const credentials = getClientCredentials(environment);
    if (credentials) {
      xhr.setRequestHeader('X-Client-Id', credentials.clientId);
      if (credentials.clientSecret) {
        xhr.setRequestHeader('X-Client-Secret', credentials.clientSecret);
      }
    }
    xhr.setRequestHeader('X-User-Id', input.userId);

    xhr.upload.onprogress = event => {
      if (event.lengthComputable) {
        const pct = Math.round((event.loaded * 100) / event.total);
        onProgress(pct, 'Uploading');
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        onProgress(100, 'Completed');
        try {
          const response = xhr.responseText ? JSON.parse(xhr.responseText) : xhr.response;
          resolve(response);
        } catch (e) {
          resolve(xhr.response);
        }
      } else {
        onProgress(100, 'Failed');
        let errorMessage = 'Audio upload failed';
        try {
          const err = xhr.responseText ? JSON.parse(xhr.responseText) : null;
          if (err?.message) errorMessage = err.message;
          else if (err?.error) errorMessage = err.error;
        } catch {
          /* ignore parse error */
        }
        reject(new Error(errorMessage));
      }
    };

    xhr.onerror = () => {
      onProgress(100, 'Failed');
      reject(new Error('Audio upload failed'));
    };

    xhr.send(formData);
  });
};

export const uploadFile = (
  input: FileUploadInput,
  onProgress: (progress: number, status: string) => void // Added status parameter
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    let END_POINT = 'upload-dataset';
    switch (input.uploadType) {
      case 'DATA_SET':
        END_POINT = 'upload-dataset';
        break;
      case 'POLICY_DOCUMENT':
        END_POINT = 'upload-policy-document';
        break;
      case 'ML_MODEL':
        END_POINT = 'upload-ml-model';
        break;
      case 'LLM_MODEL':
        END_POINT = 'upload-llm-sensitive-output';
        break;
      default:
        END_POINT = 'upload-dataset';
        break;
    }

    // Append form data
    formData.append('file', input.file);
    formData.append('projectId', input.projectId);
    formData.append('userId', input.userId);
    formData.append('uploadType', input.uploadType ?? '');

    // For POLICY_DOCUMENT, domain is required and targetColumn should not be sent
    if (input.uploadType === 'POLICY_DOCUMENT') {
      // Validate required fields for policy documents
      if (!input.domain) {
        reject(new Error('Domain is required for policy document uploads'));
        return;
      }
      if (!input.projectId) {
        reject(new Error('Project ID is required for policy document uploads'));
        return;
      }
      if (!input.userId) {
        reject(new Error('User ID is required for policy document uploads'));
        return;
      }
      if (!input.file) {
        reject(new Error('File is required for policy document uploads'));
        return;
      }

      // Validate file type (PDF or DOCX)
      const fileName = input.file.name.toLowerCase();
      if (!fileName.endsWith('.pdf') && !fileName.endsWith('.docx')) {
        reject(new Error('Only PDF and DOCX files are supported for policy documents'));
        return;
      }

      // Domain is required for policy documents
      formData.append('domain', input.domain);
      // Title is optional for policy documents
      if (input.title) {
        formData.append('title', input.title);
      }
      // Do NOT append targetColumn for policy documents
    } else {
      // For other upload types (DATA_SET, ML_MODEL, etc.)
      if (input.domain) {
        formData.append('domain', input.domain);
      }
      if (input.targetColumn) {
        formData.append('targetColumn', input.targetColumn);
      }
      if (input.title) {
        formData.append('title', input.title);
      }
    }

    // Ensure proper URL construction (remove double slashes)
    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, '') || '';
    xhr.open('POST', `${baseUrl}/${END_POINT}`);

    // Add Authorization header
    const token = localStorage.getItem('accessToken');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    // Add client credentials headers based on selected environment
    const environment = (localStorage.getItem('SELECTED_ENV') || 'DEV') as 'DEV' | 'PROD';
    const credentials = getClientCredentials(environment);
    const userId = localStorage.getItem('userId');
    if (credentials) {
      xhr.setRequestHeader('X-Client-Id', credentials.clientId);
      if (credentials.clientSecret) {
        xhr.setRequestHeader('X-Client-Secret', credentials.clientSecret);
      }
    }
    if (userId) {
      xhr.setRequestHeader('X-User-Id', userId);
    }

    // Phase 1: File Upload Progress
    xhr.upload.onprogress = event => {
      if (event.lengthComputable) {
        const uploadProgress = Math.round((event.loaded * 50) / event.total); // File upload contributes 50% to the total progress
        onProgress(uploadProgress, 'Uploading');
      }
    };

    // Phase 2: API Processing Progress
    xhr.onloadstart = () => {
      onProgress(50, 'Processing'); // After upload completes, update to 50% progress
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        onProgress(100, 'Completed'); // Update to 100% when API call succeeds
        try {
          // Try to parse JSON response
          const response = xhr.responseText ? JSON.parse(xhr.responseText) : xhr.response;
          resolve(response);
        } catch (e) {
          // If parsing fails, return the raw response
          resolve(xhr.response);
        }
      } else {
        onProgress(100, 'Failed'); // Handle failure after processing
        try {
          // Try to parse error response
          const errorResponse = xhr.responseText ? JSON.parse(xhr.responseText) : null;

          // Extract detailed error message
          let errorMessage = 'Upload failed';
          if (errorResponse) {
            // Check for nested error messages
            if (errorResponse.message) {
              errorMessage = errorResponse.message;
              // If it's a 500 error with backend error details, show user-friendly message
              if (xhr.status === 500 && errorMessage.includes("name 'client' is not defined")) {
                errorMessage =
                  'Server error: Document processing service is not properly configured. Please contact support.';
              } else if (xhr.status === 500) {
                errorMessage = `Server error: ${errorMessage}. Please try again or contact support if the issue persists.`;
              }
            } else if (errorResponse.error) {
              errorMessage = errorResponse.error;
            }
          } else if (xhr.statusText) {
            errorMessage = xhr.statusText;
          }

          // Log full error details for debugging
          console.error('Upload error:', {
            status: xhr.status,
            statusText: xhr.statusText,
            response: errorResponse,
            uploadType: input.uploadType
          });

          reject(new Error(errorMessage));
        } catch (e) {
          console.error('Error parsing error response:', e);
          reject(new Error(xhr.statusText || 'Upload failed'));
        }
      }
    };

    xhr.onerror = () => {
      onProgress(100, 'Failed'); // Update progress to failed
      reject(new Error('Upload failed'));
    };

    xhr.send(formData);
  });
};
